import { useState, useEffect, useRef, useCallback } from "react";
import React from "react";
import {
  Badge,
  Box,
  IconButton,
  Drawer,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Divider,
  Button,
  Tooltip,
} from "@mui/material";
import {
  useGetNotificationsQuery,
  useMarkAsReadMutation,
  useMarkAllAsReadMutation,
  useDeleteNotificationMutation,
  useDeleteAllNotificationsMutation,
  useGetUnreadCountQuery,
} from "../../redux/slices/notifications/notificationSlice";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import io from "socket.io-client";
import "./NotificationIcon.css";

// react-icons
import { IoNotificationsOutline, IoNotificationsOffOutline } from "react-icons/io5";
import { IoMdClose } from "react-icons/io";
import { RiDeleteBin6Line, RiDeleteBinLine } from "react-icons/ri";
import { MdOutlineDoneAll } from "react-icons/md";
import { FiUserCheck, FiUserX } from "react-icons/fi";
import { BsCashCoin } from "react-icons/bs";
import { MdOutlineWarningAmber, MdOutlineCreditCard } from "react-icons/md";
import { RiRefund2Line } from "react-icons/ri";
import { HiOutlineVolumeUp, HiOutlineVolumeOff } from "react-icons/hi";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const SOCKET_URL = BASE_URL?.replace(/\/api$/, "") || "http://localhost:5000";

// ─── Sound: Web Audio API "Chime" (Apple / macOS style) ──────
// Soft, crystal 3-tone chime
function playNotificationDing() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const notes = [698.46, 880, 1046.5]; // F5, A5, C6
    const startTime = ctx.currentTime;

    notes.forEach((freq, index) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = "sine";
      osc.frequency.setValueAtTime(freq, startTime + index * 0.07);

      gain.gain.setValueAtTime(0, startTime + index * 0.07);
      gain.gain.linearRampToValueAtTime(0.2, startTime + index * 0.07 + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, startTime + index * 0.07 + 0.45);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(startTime + index * 0.07);
      osc.stop(startTime + index * 0.07 + 0.45);
    });

    setTimeout(() => ctx.close(), 800);
  } catch (e) {
    // Silently fail if audio not supported
  }
}

export default function NotificationIcon() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // ── Sound preference — persisted in localStorage ────────────
  const [soundEnabled, setSoundEnabled] = useState(() => {
    const stored = localStorage.getItem("notif_sound_enabled");
    return stored === null ? true : stored === "true";
  });

  // ── Cooldown ref: prevents sound spam on rapid notifications ─
  // Minimum 2 seconds between sounds regardless of how many arrive.
  const lastSoundAt = useRef(0);
  const SOUND_COOLDOWN_MS = 2000;

  const triggerSound = useCallback(() => {
    if (!soundEnabled) return;
    const now = Date.now();
    if (now - lastSoundAt.current < SOUND_COOLDOWN_MS) return;
    lastSoundAt.current = now;
    playNotificationDing();
  }, [soundEnabled]);

  const toggleSound = () => {
    setSoundEnabled((prev) => {
      const next = !prev;
      localStorage.setItem("notif_sound_enabled", String(next));
      return next;
    });
  };

  // ── RTK Query ───────────────────────────────────────────────
  const { data: notificationsResponse = [], refetch } =
    useGetNotificationsQuery(undefined, {
      refetchOnMountOrArgChange: true,
    });
  const { data: unreadCountResponse = 0, refetch: refetchUnread } =
    useGetUnreadCountQuery(undefined, {
      refetchOnMountOrArgChange: true,
    });
  const [markAsRead] = useMarkAsReadMutation();
  const [markAllAsRead] = useMarkAllAsReadMutation();
  const [deleteNotification] = useDeleteNotificationMutation();
  const [deleteAllNotifications] = useDeleteAllNotificationsMutation();

  const notifications =
    notificationsResponse?.data || notificationsResponse || [];
  const unreadCount = unreadCountResponse?.count ?? 0;

  // ── Socket.IO ────────────────────────────────────────────────
  useEffect(() => {
    const newSocket = io(SOCKET_URL, {
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 5,
    });

    newSocket.on("connect", () => {
      console.log("Socket connected:", newSocket.id);
    });

    newSocket.on("connect_error", (error) => {
      console.error("Socket connection error:", error);
    });

    newSocket.on("notification", (notification) => {
      refetch();
      refetchUnread();

      let currentUserId = null;
      const token = localStorage.getItem("token");
      if (token) {
        try {
          const decoded = jwtDecode(token);
          currentUserId = decoded?.id;
        } catch (e) {}
      }

      const isSelfLogout =
        notification?.type === "EMPLOYEE_LOGOUT" &&
        notification?.relatedId != null &&
        String(notification.relatedId) === String(currentUserId);

      if (!isSelfLogout) {
        triggerSound();
      }
    });

    return () => {
      newSocket.disconnect();
    };
  }, [refetch, refetchUnread, triggerSound]);

  // ── Handlers ─────────────────────────────────────────────────
  const handleClick = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleNotificationClick = async (notification) => {
    if (!notification.isRead) {
      await markAsRead(notification.id).unwrap();
      refetch();
      refetchUnread();
    }

    switch (notification.type) {
      case "EMPLOYEE_LOGIN":
      case "EMPLOYEE_LOGOUT":
        navigate("/admin/manage-employees", {
          state: { highlightId: notification.relatedId },
        });
        break;
      case "SALE_CREATED":
        navigate("/admin/total-payments", {
          state: { highlightId: notification.relatedId },
        });
        break;
      case "SALE_REFUNDED":
        navigate("/admin/refunded-sales", {
          state: { highlightId: notification.relatedId },
        });
        break;
      case "STOCK_OUT":
        navigate("/admin/out-of-stock-products", {
          state: { highlightId: notification.relatedId },
        });
        break;
      case "VPS_PAYMENT_REMINDER":
        window.open("https://my.interserver.net/login.php", "_blank");
        break;
      default:
        break;
    }

    handleClose();
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead().unwrap();
    refetch();
    refetchUnread();
  };

  const handleDeleteNotification = async (id) => {
    await deleteNotification(id).unwrap();
    refetch();
    refetchUnread();
  };

  const handleDeleteAll = async () => {
    await deleteAllNotifications().unwrap();
    refetch();
    refetchUnread();
  };

  // ── Icon & Color maps ─────────────────────────────────────────
  const getNotificationIcon = (type) => {
    const s = { size: 20, style: { color: "#fff" } };
    switch (type) {
      case "EMPLOYEE_LOGIN":   return <FiUserCheck {...s} />;
      case "EMPLOYEE_LOGOUT":  return <FiUserX {...s} />;
      case "SALE_CREATED":     return <BsCashCoin {...s} />;
      case "SALE_REFUNDED":    return <RiRefund2Line {...s} />;
      case "STOCK_OUT":        return <MdOutlineWarningAmber {...s} />;
      case "VPS_PAYMENT_REMINDER": return <MdOutlineCreditCard {...s} />;
      default:                 return <IoNotificationsOutline {...s} />;
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case "EMPLOYEE_LOGIN":       return "linear-gradient(135deg, #43a047, #66bb6a)";
      case "EMPLOYEE_LOGOUT":      return "linear-gradient(135deg, #fb8c00, #ffa726)";
      case "SALE_CREATED":         return "linear-gradient(135deg, #1e88e5, #42a5f5)";
      case "SALE_REFUNDED":        return "linear-gradient(135deg, #e53935, #ef5350)";
      case "STOCK_OUT":            return "linear-gradient(135deg, #8e24aa, #ab47bc)";
      case "VPS_PAYMENT_REMINDER": return "linear-gradient(135deg, #d81b60, #ec407a)";
      default:                     return "linear-gradient(135deg, #546e7a, #78909c)";
    }
  };

  const getNotificationLabel = (type) => {
    switch (type) {
      case "EMPLOYEE_LOGIN":       return "İşçi girişi";
      case "EMPLOYEE_LOGOUT":      return "İşçi çıxışı";
      case "SALE_CREATED":         return "Yeni satış";
      case "SALE_REFUNDED":        return "Satış geri qaytarıldı";
      case "STOCK_OUT":            return "Stok bitdi";
      case "VPS_PAYMENT_REMINDER": return "VPS ödəniş xatırlatması";
      default:                     return "Bildiriş";
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "İndi";
    if (diffMins < 60) return `${diffMins} dəqiqə əvvəl`;
    if (diffHours < 24) return `${diffHours} saat əvvəl`;
    if (diffDays < 7) return `${diffDays} gün əvvəl`;
    return date.toLocaleDateString("az-AZ");
  };

  // ─────────────────────────────────────────────────────────────
  return (
    <Box className="notification-icon-container">
      <Tooltip title="Bildirişlər" placement="bottom">
        <IconButton onClick={handleClick} className="notification-button">
          <Badge badgeContent={unreadCount} color="error" max={99}>
            <IoNotificationsOutline className="notification-bell" size={24} />
          </Badge>
        </IconButton>
      </Tooltip>

      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        className="notification-drawer"
        PaperProps={{ className: "notification-drawer-paper" }}
      >
        <Box className="notification-drawer-content">

          {/* ── Header ── */}
          <Box className="notification-header">
            <Box className="notification-header-left">
              <IoNotificationsOutline size={20} className="header-bell-icon" />
              <Typography variant="h6" className="notification-title">
                Bildirişlər
              </Typography>
              {unreadCount > 0 && (
                <Box className="unread-badge-chip">{unreadCount} yeni</Box>
              )}
            </Box>

            <Box className="notification-header-right">
              {/* Sound Toggle */}
              <Tooltip title={soundEnabled ? "Səsi söndür" : "Səsi aç"}>
                <IconButton
                  onClick={toggleSound}
                  className={`sound-toggle-btn ${soundEnabled ? "sound-on" : "sound-off"}`}
                  size="small"
                >
                  {soundEnabled
                    ? <HiOutlineVolumeUp size={18} />
                    : <HiOutlineVolumeOff size={18} />
                  }
                </IconButton>
              </Tooltip>

              {/* Close */}
              <Tooltip title="Bağla">
                <IconButton onClick={handleClose} className="close-drawer-btn" size="small">
                  <IoMdClose size={20} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* ── Action Buttons ── */}
          {(unreadCount > 0 || notifications.length > 0) && (
            <Box className="notification-header-actions">
              {unreadCount > 0 && (
                <Tooltip title="Hamısını oxunmuş kimi işarələ">
                  <Button
                    size="small"
                    onClick={handleMarkAllAsRead}
                    className="mark-read-btn"
                    startIcon={<MdOutlineDoneAll size={15} />}
                  >
                    Hamısını oxu
                  </Button>
                </Tooltip>
              )}
              {notifications.length > 0 && (
                <Tooltip title="Bütün bildirişləri sil">
                  <Button
                    size="small"
                    onClick={handleDeleteAll}
                    className="delete-all-btn"
                    startIcon={<RiDeleteBin6Line size={14} />}
                  >
                    Hamısını sil
                  </Button>
                </Tooltip>
              )}
            </Box>
          )}

          <Divider />

          {/* ── Notification List ── */}
          <List className="notification-list">
            {notifications.length === 0 ? (
              <Box className="no-notifications">
                <IoNotificationsOffOutline size={44} className="empty-icon" />
                <Typography variant="body2" className="empty-text">
                  Bildiriş yoxdur
                </Typography>
              </Box>
            ) : (
              notifications.map((notification) => (
                <React.Fragment key={notification.id}>
                  <ListItem
                    className={`notification-item ${
                      notification.isRead ? "read" : "unread"
                    }`}
                    secondaryAction={
                      <Tooltip title="Sil">
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteNotification(notification.id);
                          }}
                          className="delete-notification-btn"
                        >
                          <RiDeleteBinLine size={16} />
                        </IconButton>
                      </Tooltip>
                    }
                  >
                    <ListItemButton
                      onClick={() => handleNotificationClick(notification)}
                      className="notification-item-button"
                    >
                      <Box
                        className="notif-icon-circle"
                        style={{ background: getNotificationColor(notification.type) }}
                      >
                        {getNotificationIcon(notification.type)}
                      </Box>
                      <ListItemText
                        primary={
                          <Box className="notification-content">
                            <Typography
                              variant="subtitle2"
                              className="notification-message"
                            >
                              {notification.message}
                            </Typography>
                            <Typography
                              variant="caption"
                              className="notification-time"
                            >
                              {formatTime(notification.createdAt)}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Typography
                            variant="caption"
                            className="notification-type-label"
                          >
                            {getNotificationLabel(notification.type)}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                  <Divider />
                </React.Fragment>
              ))
            )}
          </List>
        </Box>
      </Drawer>
    </Box>
  );
}
